import { useState } from 'react'
import { Check, X, Mail, Phone, MessageSquare, Inbox } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  approveRegistration,
  rejectRegistration,
} from '@/supabase-client'

function RejectForm({ onCancel, onConfirm, busy }) {
  const [reason, setReason] = useState('')
  return (
    <div className="mt-2 pt-2 border-t border-border/60">
      <label className="block text-xs text-muted-foreground mb-1">
        Reason for rejection (optional — stored internally)
      </label>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={2}
        className="w-full text-sm rounded border border-input px-2 py-1 focus:outline-none focus:ring-1 focus:ring-ring"
      />
      <div className="mt-2 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          size="sm"
          disabled={busy}
          onClick={() => onConfirm(reason.trim() || null)}
        >
          {busy ? 'Rejecting…' : 'Confirm reject'}
        </Button>
      </div>
    </div>
  )
}

export default function PendingRegistrationsPanel({ rows }) {
  const [busyId, setBusyId] = useState(null)
  const [rejectingId, setRejectingId] = useState(null)
  const [error, setError] = useState(null)

  const handleApprove = async (id) => {
    setError(null)
    setBusyId(id)
    try {
      await approveRegistration(id)
    } catch (e) {
      setError(`Approve failed: ${e.message}`)
    } finally {
      setBusyId(null)
    }
  }

  const handleReject = async (id, reason) => {
    setError(null)
    setBusyId(id)
    try {
      await rejectRegistration(id, reason)
      setRejectingId(null)
    } catch (e) {
      setError(`Reject failed: ${e.message}`)
    } finally {
      setBusyId(null)
    }
  }

  if (rows.length === 0 && !error) return null

  return (
    <section className="mb-6">
      <div className="flex items-center gap-2 mb-2 px-1">
        <Inbox className="size-3.5 text-blue-600" />
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Pending registration {rows.length === 1 ? 'request' : 'requests'}
        </h3>
        <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-semibold rounded-full bg-blue-600 text-white">
          {rows.length}
        </span>
      </div>

      {error && (
        <div className="mb-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          {error}
        </div>
      )}

      <div className="divide-y divide-border rounded-lg border border-border bg-card">
        {rows.map((row) => {
          const isBusy = busyId === row.id
          const isRejecting = rejectingId === row.id
          return (
            <div key={row.id} className="px-4 py-3">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-foreground">
                      {row.business_name}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {row.contact_name}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Mail className="size-3" /> {row.email}
                    </span>
                    {row.phone && (
                      <span className="inline-flex items-center gap-1">
                        <Phone className="size-3" /> {row.phone}
                      </span>
                    )}
                    {row.message && (
                      <span className="inline-flex items-center gap-1 max-w-xl truncate" title={row.message}>
                        <MessageSquare className="size-3 shrink-0" />
                        <span className="truncate">{row.message}</span>
                      </span>
                    )}
                  </div>
                </div>

                {!isRejecting && (
                  <div className="flex gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isBusy}
                      onClick={() => handleApprove(row.id)}
                      className="border-green-600 text-green-700 hover:bg-green-50 hover:text-green-800"
                    >
                      <Check className="size-3.5 mr-1" />
                      {isBusy ? 'Approving…' : 'Accept'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isBusy}
                      onClick={() => setRejectingId(row.id)}
                      className="border-red-600 text-red-700 hover:bg-red-50 hover:text-red-800"
                    >
                      <X className="size-3.5 mr-1" />
                      Reject
                    </Button>
                  </div>
                )}
              </div>

              {isRejecting && (
                <RejectForm
                  busy={isBusy}
                  onCancel={() => setRejectingId(null)}
                  onConfirm={(reason) => handleReject(row.id, reason)}
                />
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}
